/**
 * API Utility with Global Error Handling
 *
 * Provides fetch wrapper that:
 * - Shows toast notifications for errors
 * - Logs errors to backend for owner review
 * - Auto-retries on network failures
 * - Includes auth headers automatically
 */

import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Global token getter - set this once at app startup
let globalTokenGetter: (() => Promise<string | null>) | null = null;

/**
 * Set the global auth token getter.
 * Call this once at app startup with the Clerk getToken function.
 */
export function setAuthTokenGetter(getter: () => Promise<string | null>) {
  globalTokenGetter = getter;
  console.log('Auth token getter registered');
}

/**
 * Get the current auth token
 */
export async function getAuthToken(): Promise<string | null> {
  if (globalTokenGetter) {
    try {
      return await globalTokenGetter();
    } catch (e) {
      console.warn('Failed to get auth token:', e);
      return null;
    }
  }
  return null;
}

interface ApiOptions extends RequestInit {
  showErrorToast?: boolean;
  retries?: number;
  logError?: boolean;
  skipAuth?: boolean; // Skip auto-auth for this request
}

interface ApiError {
  status: number;
  message: string;
  endpoint: string;
  timestamp: string;
  details?: any;
}

/**
 * Log error to backend for owner/admin review
 */
async function logErrorToBackend(error: ApiError, token?: string): Promise<void> {
  try {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    await fetch(`${BACKEND_URL}/api/v1/errors/log`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        ...error,
        user_agent: navigator.userAgent,
        url: window.location.href,
      }),
    });
  } catch (e) {
    // Silent fail - don't show error for error logging
    console.error('Failed to log error:', e);
  }
}

/**
 * Get user-friendly error message from API response
 */
function getErrorMessage(status: number, data: any): string {
  // Check for custom error message from backend
  if (data?.detail) {
    return typeof data.detail === 'string' ? data.detail : JSON.stringify(data.detail);
  }
  if (data?.message) {
    return data.message;
  }
  if (data?.error) {
    return data.error;
  }

  // Default messages by status code
  switch (status) {
    case 400:
      return 'Invalid request. Please check your input.';
    case 401:
      return 'Session expired. Please sign in again.';
    case 403:
      return 'You don\'t have permission to perform this action.';
    case 404:
      return 'The requested resource was not found.';
    case 409:
      return 'This action conflicts with existing data.';
    case 422:
      return 'The provided data is invalid.';
    case 429:
      return 'Too many requests. Please wait a moment.';
    case 500:
      return 'Server error. Our team has been notified.';
    case 502:
    case 503:
    case 504:
      return 'Service temporarily unavailable. Please try again.';
    default:
      return `Request failed (Error ${status})`;
  }
}

/**
 * Enhanced fetch wrapper with error handling
 */
export async function apiFetch<T = any>(
  endpoint: string,
  options: ApiOptions = {}
): Promise<T> {
  const {
    showErrorToast = true,
    retries = 0,
    logError = true,
    skipAuth = false,
    ...fetchOptions
  } = options;

  const url = endpoint.startsWith('http') ? endpoint : `${BACKEND_URL}${endpoint}`;
  let lastError: Error | null = null;

  // Auto-inject auth token if available and not skipped
  if (!skipAuth) {
    const token = await getAuthToken();
    if (token) {
      fetchOptions.headers = {
        ...fetchOptions.headers,
        'Authorization': `Bearer ${token}`
      };
    }
  }

  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      const response = await fetch(url, fetchOptions);

      // Handle non-OK responses
      if (!response.ok) {
        let errorData: any = {};
        try {
          errorData = await response.json();
        } catch {
          // Response might not be JSON
        }

        const errorMessage = getErrorMessage(response.status, errorData);
        
        // Log error for owner review
        if (logError && response.status >= 500) {
          const apiError: ApiError = {
            status: response.status,
            message: errorMessage,
            endpoint: endpoint,
            timestamp: new Date().toISOString(),
            details: errorData,
          };
          
          // Get token from options if available
          const authHeader = fetchOptions.headers as Record<string, string>;
          const token = authHeader?.['Authorization']?.replace('Bearer ', '');
          logErrorToBackend(apiError, token);
        }

        // Show toast for user
        if (showErrorToast) {
          if (response.status >= 500) {
            toast.error('Something went wrong', {
              description: errorMessage,
            });
          } else if (response.status === 401) {
            toast.error('Authentication required', {
              description: errorMessage,
            });
          } else if (response.status === 403) {
            toast.error('Access denied', {
              description: errorMessage,
            });
          } else {
            toast.error('Request failed', {
              description: errorMessage,
            });
          }
        }

        throw new Error(errorMessage);
      }

      // Parse successful response
      const contentType = response.headers.get('content-type');
      if (contentType?.includes('application/json')) {
        return await response.json();
      }
      
      // Return blob for file downloads
      if (contentType?.includes('application/pdf') || 
          contentType?.includes('application/octet-stream') ||
          contentType?.includes('application/vnd')) {
        return await response.blob() as unknown as T;
      }

      return await response.text() as unknown as T;

    } catch (error: any) {
      lastError = error;
      
      // Network error - retry if attempts remaining
      if (error.name === 'TypeError' && attempt < retries) {
        await new Promise(resolve => setTimeout(resolve, 1000 * (attempt + 1)));
        continue;
      }

      // Network error on final attempt
      if (error.name === 'TypeError') {
        if (showErrorToast) {
          toast.error('Network error', {
            description: 'Please check your internet connection.',
          });
        }
      }

      throw error;
    }
  }

  throw lastError || new Error('Request failed');
}

/**
 * Convenience methods
 */
export const api = {
  get: <T = any>(endpoint: string, options?: ApiOptions) =>
    apiFetch<T>(endpoint, { ...options, method: 'GET' }),

  post: <T = any>(endpoint: string, body?: any, options?: ApiOptions) =>
    apiFetch<T>(endpoint, {
      ...options,
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...options?.headers,
      },
      body: body ? JSON.stringify(body) : undefined,
    }),

  put: <T = any>(endpoint: string, body?: any, options?: ApiOptions) =>
    apiFetch<T>(endpoint, {
      ...options,
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...options?.headers,
      },
      body: body ? JSON.stringify(body) : undefined,
    }),

  delete: <T = any>(endpoint: string, options?: ApiOptions) =>
    apiFetch<T>(endpoint, { ...options, method: 'DELETE' }),

  /**
   * Upload file with FormData
   */
  upload: <T = any>(endpoint: string, formData: FormData, options?: ApiOptions) =>
    apiFetch<T>(endpoint, {
      ...options,
      method: 'POST',
      body: formData,
      // Don't set Content-Type - browser will set it with boundary
    }),
};

/**
 * Show success toast
 */
export function showSuccess(message: string, description?: string) {
  toast.success(message, { description });
}

/**
 * Show error toast
 */
export function showError(message: string, description?: string) {
  toast.error(message, { description });
}

/**
 * Show warning toast
 */
export function showWarning(message: string, description?: string) {
  toast.warning(message, { description });
}

/**
 * Show info toast
 */
export function showInfo(message: string, description?: string) {
  toast.info(message, { description });
}

/**
 * Upload files with FormData (special case for file uploads)
 * This bypasses the JSON body conversion for FormData support
 */
export async function uploadFiles(
  endpoint: string,
  formData: FormData,
  options: { showErrorToast?: boolean } = {}
): Promise<any> {
  const { showErrorToast = true } = options;
  
  const fullUrl = `${BACKEND_URL}${endpoint}`;
  console.log(`📤 uploadFiles called`);
  console.log(`📤 BACKEND_URL: "${BACKEND_URL}"`);
  console.log(`📤 endpoint: "${endpoint}"`);
  console.log(`📤 Full URL: "${fullUrl}"`);
  
  // Debug formData contents
  const formEntries: string[] = [];
  formData.forEach((value, key) => {
    if (value instanceof File) {
      formEntries.push(`${key}: File(${value.name}, ${value.size} bytes)`);
    } else {
      formEntries.push(`${key}: ${String(value).substring(0, 100)}`);
    }
  });
  console.log(`📤 FormData contents:`, formEntries);
  
  try {
    const response = await fetch(fullUrl, {
      method: 'POST',
      body: formData,
    });
    
    console.log(`📤 Response status: ${response.status} ${response.statusText}`);
    console.log(`📤 Response headers:`, Object.fromEntries(response.headers.entries()));
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error(`❌ Response error body:`, errorText);
      let errorData = {};
      try { errorData = JSON.parse(errorText); } catch {}
      const message = getErrorMessage(response.status, errorData);
      console.error(`❌ uploadFiles error: ${message}`);
      if (showErrorToast) {
        toast.error('Upload failed', { description: message });
      }
      throw new Error(message);
    }
    
    const responseText = await response.text();
    console.log(`📤 Response body:`, responseText.substring(0, 500));
    
    let result;
    try {
      result = JSON.parse(responseText);
    } catch (e) {
      console.error(`❌ Failed to parse JSON:`, e);
      throw new Error('Invalid JSON response from server');
    }
    
    console.log(`✅ uploadFiles success:`, result);
    return result;
  } catch (error) {
    console.error(`❌ uploadFiles exception:`, error);
    if (showErrorToast && error instanceof Error) {
      toast.error('Upload failed', { description: error.message });
    }
    throw error;
  }
}

export default api;
